import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, getMe } from '../services/api';
import { connectSocket, disconnectSocket } from '../services/socket';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar sesion al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await getMe();
          setUser(response.data.user);
          connectSocket(response.data.user.id, response.data.user.user_departments || []);
        } catch (err) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await apiLogin(email, password);
      const { token, user: userData } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      // Conectar socket
      connectSocket(userData.id, userData.departments || []);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Error al iniciar sesion';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    disconnectSocket();
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasAnyRole = (...roles) => {
    return roles.includes(user?.role);
  };

  const hasDepartmentAccess = (departmentId) => {
    if (hasAnyRole('superadmin', 'admin')) return true;
    return user?.user_departments?.some(ud => ud.department_id === departmentId);
  };

  const getDepartmentRole = (departmentId) => {
    if (hasRole('superadmin')) return 'admin';
    const dept = user?.user_departments?.find(ud => ud.department_id === departmentId);
    return dept?.role || null;
  };

  // Jerarquía de roles (ENS alto)
  const ROLE_LEVELS = {
    superadmin: 100,
    admin: 90,
    revisor: 70,
    usuario: 50,
    franquiciado: 40,
    tecnico_externo: 30,
    cuenta_cliente: 20,
    cliente_final: 10
  };

  const getRoleLevel = () => {
    return ROLE_LEVELS[user?.role] || 0;
  };

  const isAtLeast = (role) => {
    return getRoleLevel() >= (ROLE_LEVELS[role] || 0);
  };

  // Permisos específicos (solo revisor+ puede alimentar IA)
  const canFeedAI = () => isAtLeast('revisor');
  const canManageUsers = () => isAtLeast('admin');
  const canDeleteUsers = () => isAtLeast('superadmin');
  const canAccessReports = () => isAtLeast('revisor');
  const canConfigureSystem = () => isAtLeast('admin');

  // Actualizar datos del usuario en el contexto
  const updateUserData = (newData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...newData
    }));
    // Actualizar también en localStorage
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.setItem('user', JSON.stringify({ ...storedUser, ...newData }));
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    hasRole,
    hasAnyRole,
    hasDepartmentAccess,
    getDepartmentRole,
    isAuthenticated: !!user,
    updateUserData,
    // Nuevas funciones de permisos (ENS alto)
    getRoleLevel,
    isAtLeast,
    canFeedAI,
    canManageUsers,
    canDeleteUsers,
    canAccessReports,
    canConfigureSystem,
    ROLE_LEVELS
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
